import { LandingPageSections } from '../LandingPage.types'
import { CurrentUser } from '../../global'
import { mobileAppDownloadPath } from 'lib/urls'
import { browserName } from 'lib/bowser'
import { BABYLIST_EXTENSION_URL } from 'shared/constants'

const buildRegistryRedirect = (currentUser: CurrentUser | undefined) => {
  if (currentUser) {
    // TODO: update this to /registry/add-items once that page is created
    window.location.href = '/registry/home'
  } else {
    window.location.href = '/registry/new'
  }
}

const whyBabylistPageSections: LandingPageSections[] = [
  {
    type: 'hero',
    id: '1',
    desktopWidth: 'full',
    images: {
      desktop:
        'https://images.babylist.com/image/upload/q_auto/v1710885982/Landing%20Page/Why%20Babylist/why_babylist_desktop_hero_no_curve.png',
      mobile:
        'https://images.babylist.com/image/upload/q_auto/v1710181485/Landing%20Page/Why%20Babylist/why_babylist_mobile_hero.png',
    },
    orientation: 'overlay',
    headerText: 'The Best Universal Baby Registry',
    subHeaderText: `We're on a mission to make your journey to parenthood easier with a registry that helps you plan, prepare, and shop for your new arrival.`,
    mobileSubHeaderText: `We're on a mission to make it easier for you to plan, prepare and shop for your new arrival. `,
    callToAction: {
      type: 'link',
      id: '1-1',
      text: 'Build Your Registry',
      onClick: buildRegistryRedirect,
    },
    desktopContentIsRightAligned: true,
    isLongerImage: true,
    curvedBottom: true,
  },
  {
    type: 'feature_highlight',
    id: '2',
    background: 'none',
    imagePlacement: 'right',
    desktopWidth: 'full',
    imageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1711041079/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_1_desktop.png',
    mobileImageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1709917414/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_1.png',
    imageAltText:
      'A room filled with items from various websites, such as Babylist, Ikea, Nordstrom, Etsy, and Target.',
    headerText: 'Add Anything to Your Registry From Any Store',
    description: {
      content: [
        `Fill your registry with gifts from any websites including Amazon, Target, Etsy, Crate & Kids, Nordstrom, H&M, and Walmart–just to name a few.`,
      ],
      renderAsListItems: false,
    },
    layout: 'stacked',
    callToAction: {
      type: 'link',
      id: '2-1',
      text: 'Get Started',
      onClick: buildRegistryRedirect,
    },
    backgroundColor: 'white',
    isRectangleImage: false,
  },
  {
    type: 'text',
    id: '3',
    body: `
      "Babylist is so much more than a registry, <b>they become a part of your journey</b> into parenthood."
      <br />
      - Caitlin C.
    `,
    background: 'blueToPurple',
    useWaveBorders: true,
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'center',
    desktopWidth: 'full',
  },
  {
    type: 'feature_highlight',
    id: '4',
    background: 'none',
    imagePlacement: 'left',
    desktopWidth: 'full',
    imageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1711041079/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_2_desktop.png',
    mobileImageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1709917414/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_2.png',
    imageAltText:
      'Modal showing a product and a various websites the product is available for purchase, along with their prices.',
    headerText: 'Instantly Compare Prices Across Dozens of Stores',
    description: {
      content: [
        `We'll show you when items on your registry are available at other retailers so friends and family can decide where to buy based on the lowest price or shop from their favorite store.`,
      ],
      renderAsListItems: false,
    },
    layout: 'stacked',
    callToAction: {
      type: 'link',
      id: '4-1',
      text: 'Get Started',
      onClick: buildRegistryRedirect,
    },
    backgroundColor: 'white',
    isRectangleImage: false,
  },
  {
    type: 'text',
    id: '5',
    body: `
      "Very intuitive to use for both my partner and I. I love that it shows prices from different stores. <b>It's so easy to add any product ever with the 'Add to Registry' button</b> when you're on any website whether you're on the app or desktop."
      <br />
      - Brandy S.
    `,
    background: 'blueToPurple',
    useWaveBorders: true,
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'center',
    desktopWidth: 'full',
  },
  {
    type: 'feature_highlight',
    id: '6',
    background: 'none',
    imagePlacement: 'right',
    desktopWidth: 'full',
    imageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1711041079/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_3_desktop.png',
    mobileImageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1709917414/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_3.png',
    imageAltText:
      'Screenshots of the Babylist app, demonstrating editorials, checklist, and registry features.',
    headerText: 'Plan for the Future with an App That Does It All',
    description: {
      content: [
        `Browse popular brands and stores, quickly add gifts to your registry, manage your checklist, and find helpful resources to support you on your journey.`,
      ],
      renderAsListItems: false,
    },
    layout: 'stacked',
    callToAction: {
      type: 'link',
      id: '6-1',
      text: 'Download The App',
      onClick: () => {
        window.open(mobileAppDownloadPath('', 'apple'), '_blank')
      },
    },
    backgroundColor: 'white',
    isRectangleImage: false,
  },
  {
    type: 'text',
    id: '7',
    body: `
      "Absolutely the best registry app I've ever used. Very helpful with suggestive ideas, motivational comments, easy access to other websites to <b>add items directly onto your registry with just one click.</b>"
      <br />
      - Rachel P
    `,
    background: 'blueToPurple',
    useWaveBorders: true,
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'center',
    desktopWidth: 'full',
  },
  {
    type: 'feature_highlight',
    id: '8',
    background: 'none',
    imagePlacement: 'left',
    desktopWidth: 'full',
    imageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1711041079/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_4_desktop.png',
    mobileImageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1709917414/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_4_mobile.png',
    imageAltText:
      'Demonstration of using the Babylist Save Button to add an item to a registry.',
    headerText: 'Our Quick-Save Button Lets You Add Items From Any Website',
    description: {
      content: [
        `Install the Babylist Save Button to add any item to your registry from anywhere on the web.`,
      ],
      renderAsListItems: false,
    },
    layout: 'stacked',
    callToAction: {
      type: 'link',
      id: '8-1',
      text: 'Add To Your Browser',
      onClick: () => {
        const url =
          browserName === 'Chrome' ? BABYLIST_EXTENSION_URL : '/add-items'
        window.open(url, '_blank')
      },
    },
    backgroundColor: 'white',
    isRectangleImage: false,
  },
  {
    type: 'text',
    id: '9',
    body: `
      "The registry guides have been extremely helpful so <b>we can choose what's best for our family</b> and immediately add to the registry all in one app."
      <br />
      - Ryan W.
    `,
    background: 'blueToPurple',
    useWaveBorders: true,
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'center',
    desktopWidth: 'full',
  },
  {
    type: 'feature_highlight',
    id: '10',
    background: 'none',
    imagePlacement: 'right',
    desktopWidth: 'full',
    imageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1711041079/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_5_desktop.png',
    mobileImageUrl:
      'https://images.babylist.com/image/upload/q_auto/v1710184506/Landing%20Page/Why%20Babylist/why_babylist_feature_highlight_5_3x.png',
    imageAltText:
      'A person holding a phone with the Babylist app, transferring an external registry to Babylist.',
    headerText: 'Combine Registries to Manage Everything in One Place',
    description: {
      content: [
        `Already built a baby registry somewhere else? Transfer your items from another registry or just add a link. We'll keep it all organized for you so you can focus on the fun part—picking items to add!`,
      ],
      renderAsListItems: false,
    },
    layout: 'stacked',
    callToAction: {
      type: 'link',
      id: '10-1',
      text: 'Transfer Another Registry',
      onClick: (currentUser) => {
        if (currentUser) {
          window.location.href = '/registry/external'
        } else {
          window.location.href = '/registry/new'
        }
      },
    },
    backgroundColor: 'white',
    isRectangleImage: false,
  },
  {
    type: 'final_cta',
    id: '11',
    heading: `You're Baby Steps Away From Building Your Registry`,
    background: 'orange violet gradient',
    backgroundColor: 'white',
    textColor: 'eggplant',
    callToAction: {
      type: 'link',
      id: '11-1',
      text: 'Start Your Babylist',
      onClick: buildRegistryRedirect,
    },
  },
]

export default whyBabylistPageSections
